// @flow
import { forwardRef, memo } from 'react'

import styled, { css } from 'styled-components'

type Direction = 'row' | 'row-reverse' | 'column' | 'column-reverse'

type Wrap = 'nowrap' | 'wrap' | 'wrap-reverse'

type JustifyContent =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | 'stretch'
  | 'start'
  | 'end'
  | 'left'
  | 'right'
  | 'safe'
  | 'unsafe'

type AlignItems =
  | 'stretch'
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'baseline'
  | 'first baseline'
  | 'last baseline'
  | 'start'
  | 'end'
  | 'self-start'
  | 'self-end'
  | 'safe'
  | 'unsafe'

type AlignContent =
  | 'flex-start'
  | 'flex-end'
  | 'center'
  | 'space-between'
  | 'space-around'
  | 'space-evenly'
  | 'stretch'
  | 'start'
  | 'end'
  | 'baseline'
  | 'first baseline'
  | 'last baseline'
  | 'safe'
  | 'unsafe'

type Order = number

type Grow = number

type Shrink = number

type Basis = number | string | 'auto'

type AlignSelf = 'auto' | 'flex-start' | 'flex-end' | 'center' | 'baseline' | 'stretch'

export type FlexComponentProps = {|
  direction?: Direction,
  wrap?: Wrap,
  justifyContent?: JustifyContent,
  alignItems?: AlignItems,
  alignContent?: AlignContent,
  order?: Order,
  grow?: Grow,
  shrink?: Shrink,
  basis?: Basis,
  alignSelf?: AlignSelf,
  className?: string,
  children?: React$Node,
  onClick?: (SyntheticEvent<HTMLDivElement>) => void,
|}

const StyledRoot: React$AbstractComponent<FlexComponentProps, HTMLElement> = styled.div.withConfig({
  shouldForwardProp: (name, defaultValidatorFn) =>
    ['className', 'children', 'onClick', 'ref'].includes(name) && defaultValidatorFn(name),
})`
  display: flex;
  ${(props: FlexComponentProps) => css`
    flex-direction: ${props.direction ?? 'row'};
    justify-content: ${props.justifyContent};
    align-items: ${props.alignItems};
    align-content: ${props.alignContent};
    order: ${props.order};
    flex-grow: ${props.grow};
    flex-shrink: ${props.shrink};
    flex-basis: ${props.basis};
    align-self: ${props.alignSelf};
    flex-wrap: ${props.wrap};
  `}
`

const Flex = ({ children, ...props }: FlexComponentProps, ref) => {
  return (
    <StyledRoot {...props} ref={ref}>
      {children}
    </StyledRoot>
  )
}

export const FlexSpacer = styled.span`
  flex: 1;
  width: 100%;
`

const FlexMemoForward = forwardRef<FlexComponentProps, HTMLElement>(Flex)

const FlexMemo = memo<FlexComponentProps>(FlexMemoForward)

export { FlexMemo as Flex }
